/* eslint-disable camelcase */
import { resolve, ImResponse, doorClient, storeHomeClient, storeHomeV2Client } from '.';

export interface ListParams {
  region: General.UpperCaseRegion;
  externalType?: General.PlatformUpperCase;
  permId?: General.PermissionId;
}

export function list(params: ListParams) {
  return resolve(
    storeHomeClient.get<ImResponse<Store.Store[]>>('stores', {
      params,
    }),
  );
}

export interface StoreNameConfig {
  id: string;
  platform: General.PlatformUpperCase;
  orgId: number;
  region: General.UpperCaseRegion;
  aliasFlag: boolean;
}
export function getStoreNameConfig() {
  return resolve(
    doorClient.post<ImResponse<StoreNameConfig[]>>(
      `overseaim-store-home/org-region-config/v1/query`,
      {},
    ),
  );
}

export function updateStoreNameConfig(id: string, aliasFlag: boolean) {
  return resolve(
    doorClient.post<ImResponse<Store.Store[]>>(`overseaim-store-home/org-region-config/v1/edit`, {
      id,
      aliasFlag,
    }),
  );
}

export function createStoreNameConfig(params: {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  aliasFlag: boolean;
}) {
  return resolve(
    doorClient.post<ImResponse<Store.Store[]>>(`overseaim-store-home/org-region-config/v1/add`, {
      ...params,
    }),
  );
}

export type SearchStoreListScene =
  | 'BASIC_RECEPTION'
  | 'VIEW_REVIEW'
  | 'VIEW_CANCEL_ORDER'
  | 'VISIT_TODO_MANAGEMENT_CENTER'
  | 'ADMIN'
  | 'VIEW_AI_QA_DATA'
  | 'PRODUCT_KNOWLEDGE'
  | 'SENSITIVE_WORDS';

export interface SearchStoreListParams {
  tunnelAccountIds?: string[];
  storeIds?: number[];
  region?: General.UpperCaseRegion;
  regions?: General.UpperCaseRegion[];
  follower?: '0' | '1';

  // 根据id，店铺名，店铺别名搜索
  keyword?: string;
  platform?: General.PlatformUpperCaseTunnel;
  cursor: number;
  page_size: number;
  scene?: SearchStoreListScene;
}
export interface StoreInfo {
  storeId: string;
  region: General.UpperCaseRegion;
  storeName: string;
  alias: string;
  unreadCount: number;
  externalType: General.PlatformUpperCaseTunnel;
  follower: boolean;
  externalStoreId: string;
  tunnelAccountId: string;
  tunnelName: string;
  /**
    AUTH((byte) 2, "生效中"),
    AUTH_EXPIRE((byte) 3, "授权过期"),
    AUTH_PROCESS((byte) 4, "授权进行中"),
    AUTH_NO_PERMISSION_LOCAL_REGISTRATION((byte) 5,
        "在2022年8月4日后，当调用需要传入shop_id的OpenAPI时，新增对于新加坡、菲律宾、越南、泰国、印度尼西亚站点local店铺的卖家注册流程（在虾皮卖家中心完成）是否完成的校验"),
    GRAY_UN_AUTH((byte) 7, "灰口授权失败"),
    APP_UN_AUTH((byte) 8, "app授权失败(白口)"),
    APP_AUTH_PROCESS((byte) 9, "app授权中"),
    APP_AUTH_EXCEPTION((byte) 10, "app授权异常(例如无chat授权)"),
    WAIT_GRAY_AUTH((byte) 11, "白口授权成功,等待灰口授权");
   */
  authStatus:
    | 'UN_AUTH'
    | 'AUTH'
    | 'AUTH_EXPIRE'
    | 'AUTH_PROCESS'
    | 'AUTH_NO_PERMISSION_LOCAL_REGISTRATION'
    | 'GRAY_UN_AUTH'
    | 'APP_UN_AUTH'
    | 'APP_AUTH_PROCESS'
    | 'APP_AUTH_EXCEPTION'
    | 'WAIT_GRAY_AUTH';
}

export interface IStoreListRsp {
  data: StoreInfo[];
  hasNext: boolean;
  nextCursor: string;
}

export function searchStoreList(params: SearchStoreListParams) {
  return doorClient
    .post<ImResponse<IStoreListRsp>>('aggregation/v1/assistant_tunnels', {
      ...params,
    })
    .then((res) => {
      res?.data?.data?.data?.forEach((v) => {
        // eslint-disable-next-line no-param-reassign
        // v.storeId = v.tunnelAccountId;
        if (!v.storeName) {
          // eslint-disable-next-line no-param-reassign
          v.storeName = v.tunnelName;
        }
      });
      return res;
    });
}

export function getStoreListByIds(ids: number[]): Promise<StoreInfo[]> {
  return searchStoreList({
    cursor: 0,
    page_size: ids.length,
    storeIds: ids,
  }).then((res) => res?.data?.data?.data || []);
}

// 实际接口返回不止这些字段，但是没有文档，暂时先补充这些已知的。
export interface StoreCNSCInfo {
  externalStoreId: string;
  externalType: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  storeType: 'LOCAL' | 'CROSS';
}

export function getStoreCNSCInfoById(storeId: string) {
  return doorClient.get<{
    storeInfo: StoreCNSCInfo;
  }>('overseaim-store-home/v1/store_info', {
    params: {
      storeId,
    },
  });
}

export function changeFollowStatus(tunnelAccountId: string, follower: boolean) {
  return doorClient.put('/oversea-tunnel/updateAssistantFollower', {
    tunnelAccountId,
    follower,
  });
}

export interface GetStoreRegionInfoParams {
  departmentId?: number;
  departmentIds?: number[];
  scene?: SearchStoreListScene | 'ORDER_STAT' | 'ASSISTANT_PERFORMANCE' | 'VIEW_PLATFORM_DATA';
}

export function getStoreRegionInfo(params: GetStoreRegionInfoParams) {
  return storeHomeV2Client.get<
    ImResponse<
      {
        platform: General.PlatformUpperCaseTunnel;
        region: General.UpperCaseRegion;
        regionName: string;
      }[]
    >
  >('/admin/getStoreRegionInfo', { params });
}

export function clearAllFollowList() {
  return doorClient.post('overseaim-store-home/v1/clearUserStoreFollower', {});
}

interface UserStoreCount {
  /** 组织层级店铺数量 */
  orgLevelStoreCount: General.PlatformUpperCaseTunnel;
  /** 用户基础层级店铺数量 */
  userBasicLevelStoreCount: General.UpperCaseRegion;
  /** 会话列表是否展示 All Tab */
  showAll: boolean;
}

/** 获取登录用户的店铺数量 */
export function getUserStoreCount(signal?: AbortSignal) {
  return doorClient.get<ImResponse<UserStoreCount>>('/overseaim-store-home/get_store_count', {
    signal,
  });
}
